import { Button, Checkbox, FieldGroup, PageLoader } from '@ver-uds/react';
import { Alert, ButtonGroup, TextInputControl } from '@ver-uds/uswds-react';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import ClientMouWrapper from '../../components/ClientMou/ClientMouWrapper';
import { MouPageFormData, MouRoutes } from '../../components/ClientMou/constants';
import { ClientMouRejectionModal } from '../../components/ClientMou/MouRejectionModal';
import PaperworkReductionActModal from '../../components/ClientMou/PaperworkReductionActModal';
import MarketingQuestions from '../../components/Enrollment/CompanyInformation/MarketingQuestions/MarketingQuestions';
import { useAcceptClientMou, useRejectClientMou, useUnsignedMouDocumentQuery } from '../../services/MOUSignatureApi';
import { useMarketingChannelQuery } from '../../services/RefDataMarketingChannelApi';

const ClientSignMouPage = (): JSX.Element => {
  const [rejectionModalOpen, setRejectionModalOpen] = useState<boolean>(false);
  const [loadingMouError, setLoadingMouError] = useState<boolean>(false);
  const { code } = useParams<{ code: string }>();
  const alertRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();

  const { isLoading } = useMarketingChannelQuery();
  const formMethods = useForm<MouPageFormData>();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { errors, handleSubmit, register, watch } = formMethods;
  const parentValue = watch('marketingQuestionPrimary');
  const [acceptClientMou, { isError: isAcceptError, isLoading: isAcceptLoading }] = useAcceptClientMou({
    config: {
      onSuccess() {
        history.push(MouRoutes.ACCEPT, { code });
      },
    },
  });
  const [
    rejectClientMou,
    { isError: isRejectError, isLoading: isRejectLoading, reset: resetRejectMou },
  ] = useRejectClientMou({
    config: {
      onSuccess() {
        setRejectionModalOpen(false);
        history.push(MouRoutes.REJECT);
      },
    },
  });

  const onSubmitAgree = async (data: MouPageFormData): Promise<void> => {
    await acceptClientMou({
      clientMouAcceptRequest: {
        authCode: code,
        marketingChannel: data.marketingQuestionPrimary
          ? {
              primaryValue: data.marketingQuestionPrimary,
              secondaryValue: data.marketingQuestionSecondary || undefined,
            }
          : undefined,
      },
    });
  };

  const { refetch } = useUnsignedMouDocumentQuery(code, {
    enabled: false,
    useErrorBoundary: false,
    onError: (e) => {
      setLoadingMouError(true);
    },
  });

  const downloadUnsignedMou = async () => {
    const blobData = await refetch({ throwOnError: false });

    if (blobData && 'msSaveOrOpenBlob' in navigator && navigator.appVersion.toString().indexOf('.NET') > 0) {
      setLoadingMouError(false);
      navigator.msSaveOrOpenBlob(blobData, 'MouUnsignedDocument.pdf');
    } else if (blobData) {
      setLoadingMouError(false);
      const url = URL.createObjectURL(blobData);
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    if (alertRef.current && (errors?.agreedToTerms || errors?.infoIsCorrect)) {
      alertRef.current.focus();
    }
  }, [alertRef, errors]);

  return (
    <>
      {isLoading ? (
        <PageLoader label="Loading" />
      ) : (
        <ClientMouWrapper title="Sign Memorandum of Understanding" header="Completing the Client Enrollment Process">
          <section className="padding-y-4 grid-col-12">
            <h1>Sign Memorandum of Understanding</h1>
            <p className="usa-intro padding-bottom-2 maxw-none">
              Before agreeing to sign the Memorandum of Understanding (MOU), make sure that you have signature authority
              for your company.
            </p>
            {loadingMouError ? (
              <Alert status="error" className="margin-bottom-4">
                <Alert.Heading>We were unable to process your request</Alert.Heading>
                <Alert.Text>
                  There is an issue with your Electronic MOU link. Please contact your agent to send you a new one.
                </Alert.Text>
              </Alert>
            ) : null}
            <Button variant="secondary" type="button" className="margin-bottom-3" onClick={downloadUnsignedMou}>
              View Memorandum of Understanding
            </Button>
            <form id="client-mou-sign-form" className="maxw-none" noValidate onSubmit={handleSubmit(onSubmitAgree)}>
              <Checkbox
                id="info-is-correct"
                name="infoIsCorrect"
                ref={register({
                  required: {
                    value: true,
                    message: 'You must agree to the terms by checking both checkboxes above before proceeding.',
                  },
                })}
                required
                label={
                  <p className="line-height-1">
                    I certify that the information provided for this registration is correct. I’m aware that federal law
                    provides for imprisonment and / or fines for knowingly providing false statements and / or other
                    fraudulent conduct in connection with this registration. I’m aware that providing any false
                    information may be grounds for terminating participation in E-Verify.
                  </p>
                }
              />
              <Checkbox
                id="agreed-to-terms"
                className="margin-bottom-3"
                name="agreedToTerms"
                ref={register({
                  required: {
                    value: true,
                    message: 'You must agree to the terms by checking both checkboxes above before proceeding.',
                  },
                })}
                required
                label={
                  <>
                    <p className="padding-bottom-2">
                      I have read and agree with the terms and conditions of the MOU, and am authorized by my company to
                      act on its behalf with respect to the E-Verify program. I understand that I must complete the
                      electronic registration in order for the MOU to take effect.
                    </p>
                    <p>
                      <b>IMPORTANT:</b> The MOU sets forth my company’s responsibilities under the E-Verify program.
                      Failure to comply with the MOU may lead to legal liability for my company under federal and state
                      law, including the payment of back pay, civil monetary penalties, and other damages under Title
                      VII of the Civil Rights Act of 1964 and the anti-discrimination provision of the Immigration and
                      Nationality Act. You should print the MOU, and share it with your Human Resources Manager, legal
                      counsel, and other appropriate staff.
                    </p>
                  </>
                }
              />
              {(errors?.agreedToTerms || errors?.infoIsCorrect) && (
                <div ref={alertRef}>
                  <Alert status="error">
                    <Alert.Text>{errors?.agreedToTerms?.message || errors?.infoIsCorrect?.message}</Alert.Text>
                  </Alert>
                </div>
              )}
              <TextInputControl
                name="mouSignatory"
                id="name-input"
                label="Type your name to confirm"
                required
                ref={register({ required: { value: true, message: 'Please fill out this field' } })}
                errorId="name-input-error"
                errorMessage={errors?.mouSignatory?.message}
              />
              <FieldGroup>
                <FieldGroup.Field name="Today's Date">{format(new Date(), 'P')}</FieldGroup.Field>
              </FieldGroup>
              <hr />
              <div className="margin-bottom-4 margin-top-2">
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <FormProvider {...formMethods}>
                  <MarketingQuestions
                    parentValue={parentValue}
                    headerTitle="Additional Information"
                    questionLabel="What is the primary reason you’re enrolling in E-Verify?"
                  />
                </FormProvider>
              </div>
              <hr />
              {isAcceptError ? (
                <Alert status="error" className="margin-top-4">
                  <Alert.Heading>We were unable to process your request</Alert.Heading>
                  <Alert.Text>
                    There is an issue with your Electronic MOU link. Please contact your agent to send you a new one.
                  </Alert.Text>
                </Alert>
              ) : (
                <ButtonGroup className="padding-top-5">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      resetRejectMou();
                      setRejectionModalOpen(true);
                    }}
                  >
                    Decline MOU
                  </Button>
                  <Button variant="submit" type="submit" disabled={isAcceptLoading}>
                    Agree to MOU
                  </Button>
                </ButtonGroup>
              )}
            </form>
            <div className="display-flex flex-column flex-align-end padding-top-5">
              <PaperworkReductionActModal fixedToTop={false} />
              <p>OMB Control No. 1615-0092 • Expiration Date 5/31/2027</p>
            </div>
          </section>
          <ClientMouRejectionModal
            open={rejectionModalOpen}
            setOpen={setRejectionModalOpen}
            isError={isRejectError}
            isLoading={isRejectLoading}
            onSubmit={async (data) => {
              await rejectClientMou({
                clientMouRejectRequest: {
                  authCode: code,
                  rejectionReason: data.reason,
                },
              });
            }}
          />
        </ClientMouWrapper>
      )}
    </>
  );
};

export default ClientSignMouPage;
